import { reactive, watch, ref, nextTick } from 'vue';
import axios from 'axios';
import { Preferences } from '@capacitor/preferences';

// Cargar el carrito desde localStorage
const loadCartFromLocalStorage = () => {
  const cartData = localStorage.getItem('cart');
  return cartData ? JSON.parse(cartData) : [];
};

// --- SINGLETON STATE ---
const state = reactive({
  cart: loadCartFromLocalStorage(),
});
const multicombos = ref([]);

// Sincronizar con localStorage
watch(
  () => state.cart,
  (newCart) => {
    localStorage.setItem('cart', JSON.stringify(newCart));
  },
  { deep: true }
);


// ----------------------------------------------------------
// NORMAL addToCart
// ----------------------------------------------------------


const addToCart = (product) => {
  if (!navigator.onLine) {
    alert("Sin conexión a internet. No puedes agregar productos sin conexión.");
    return;
  }
  const basePrice = Number(product.price);
  const promoPercent = Number(product.promo_price ?? 0);

  const finalPrice =
    product.id_promocion && promoPercent > 0
      ? Number((basePrice - basePrice * (promoPercent / 100)).toFixed(2))
      : basePrice;
  const sku = product.sku ?? String(product.producto_id ?? product.id);

  const existing = state.cart.find(item => item.sku === sku && !item.isComboItem && !item.isGift);

  // 🔥 CORRECCIÓN: Detectar si es promo real basándonos en ID y porcentaje, no en la bandera 'promoUnico' que viene mal
  const isPromo = !!(product.id_promocion && promoPercent > 0);

  // Límite de promo: configurable desde el panel de administración
  const promoQtyLimit = Number(localStorage.getItem('promo_qty_limit') || 5);
  if (existing && isPromo) {
    if (existing.quantity >= promoQtyLimit) {
      alert(`Máximo ${promoQtyLimit} unidades por producto en promoción.`);
      return;
    }
  }

  // BLOQUEO PARA PRODUCTOS NORMALES (Línea 58)
  if (existing) {
    if (existing.quantity < 40) {
      existing.quantity += 1;
      validateMultiCombos();
    } else {
      alert('Solo puedes agregar hasta 40 unidades de este producto.');
    }
    return;
  }

  // 🆕 PRODUCTO NUEVO
  state.cart.push({
    ...product,

    // 🔒 PRECIO FINAL (NO SE TOCA)
    price: finalPrice,
    // Precio de catálogo (antes del descuento promo) — el backend lo usa para detectar promos
    originalPrice: basePrice,
    promoUnico: isPromo,
    quantity: 1,
    sku,
    linea_prod_id: product.linea_prod_id ?? null,
    pround: product.pround ?? 'CJA',

    iva: Number(product.iva16 ?? product.iva ?? 0),
    ieps: Number(product.ieps ?? 0),

  });

  validateMultiCombos();
  if (navigator.vibrate) navigator.vibrate(50);
};




// ----------------------------------------------------------
const lastUpdated = ref(Date.now());

const resetState = async () => {
  state.cart.splice(0, state.cart.length);
  multicombos.value = [];
  lastUpdated.value = Date.now();

  localStorage.removeItem('cart');
  localStorage.removeItem('checkoutTotal');
  localStorage.removeItem('checkoutCart');
  localStorage.removeItem('checkoutCajas');
  localStorage.removeItem('direccionSeleccionada');

  await nextTick();
};

// ----------------------------------------------------------
// PROMOCIONES addToCart2
// ----------------------------------------------------------
const addToCart2 = async (product) => {
  if (!navigator.onLine) {
    alert("Sin conexión a internet. No puedes agregar productos sin conexión.");
    return;
  }
  try {
    const { data } = await axios.get(
      `https://mvexpress.mx/api/promotion/${product.id}`
    );

    const promo = data?.[0];
    if (!promo) return;


    addToCart({
      // 🔑 Identidad real del producto
      id: promo.id_producto,
      sku: promo.PRODUC_CODIGO_K,

      // 🔥 PRECIO FINAL YA CALCULADO (NO SE TOCA)
      price: Number(promo.precio_con_descuento),

      // 🔒 PRECIO ORIGINAL SOLO INFORMATIVO
      precio_lista: Number(promo.precio_mostrador ?? promo.precio_lista ?? promo.price),

      quantity: 1,

      pround: promo.pround ?? 'CJA',
      linea_prod_id: promo.linea_prod_id,

      // 🔥 PROMO (SOLO METADATA)
      id_promocion: promo.id_promocion,
      promo_percent: promo.promo_price,   // 10
      promoUnico: true,

      // impuestos
      iva: Number(promo.iva16 ?? promo.iva ?? 0),
      ieps: Number(promo.ieps ?? 0),
    });


  } catch (err) {
    console.error(err);
    alert("No se pudo agregar la promoción");
  }
};



// ----------------------------------------------------------
// 🔥🔥🔥 addGiftToCart (PROMOCIÓN CON REGALO BLOQUEADA) 🔥🔥🔥
// ----------------------------------------------------------
const addGiftToCart = async (item) => {
  if (!navigator.onLine) {
    alert("Sin conexión a internet. No puedes agregar productos sin conexión.");
    return;
  }
  try {

    // 1️⃣ Obtener datos de la promoción
    const { data: promo } = await axios.get(`https://mvexpress.mx/api/promotion2/${item.id}`);
    if (!promo) return alert("No se encontraron detalles de la promoción.");

    const cantidad = promo.cantidad || 1;  // principal debe quedar fijo en esta cantidad
    const comboId = `combo-${promo.id}`;

    // 2️⃣ Cargar productos del combo
    const { data: mainData } = await axios.get(`https://mvexpress.mx/api/get-sku/${promo.sku_producto}`);
    const mainProduct = mainData[0];

    const { data: giftData } = await axios.get(`https://mvexpress.mx/api/get-sku/${promo.sku_regalo}`);
    const giftProduct = giftData[0];

    if (!mainProduct || !giftProduct) {
      return alert("No se pudieron obtener los productos.");
    }

    // 3️⃣ Buscar existentes
    const existingMain = state.cart.find(
      p => p.sku === mainProduct.sku && p.comboId === comboId && p.isMain
    );

    const existingGift = state.cart.find(
      p => p.sku === giftProduct.sku && p.comboId === comboId && p.isGift
    );

    // ------------------------------
    // 🔹 4️⃣ PRODUCTO PRINCIPAL (NO SE AUMENTA)
    // ------------------------------
    if (!existingMain) {
      // 🔥 CORRECCIÓN: Validación robusta del precio para evitar NaN o 0
      // A veces 'get-sku' devuelve 'precio_lista' o 'precio_1' en lugar de 'price'
      const precioBase = Number(mainProduct.precio_con_descuento) ||
        Number(mainProduct.price) ||
        Number(mainProduct.precio_lista) ||
        Number(mainProduct.precio_mostrador) ||
        0;

      // Se agrega solo una vez con la cantidad de la promo (5,10,...)
      state.cart.push({
        ...mainProduct,
        quantity: cantidad,
        comboId,
        isMain: true,
        promoUnico: true,
        price: precioBase
      });
    }
    // ❗ Si ya existe, NO aumentar más
    else {
      existingMain.quantity = cantidad; // siempre fijo
    }

    // ------------------------------
    // 🔹 5️⃣ PRODUCTO REGALO (SUMA 1 POR CLICK)
    // ------------------------------
    if (existingGift) {
      existingGift.quantity += 1; // ahora sí suma correcto
    } else {
      state.cart.push({
        ...giftProduct,
        quantity: 1,
        comboId,
        isGift: true,
        price: 0,
        nombre: `${giftProduct.nombre} 🎁`
      });
    }

    // Guardar cambios
    localStorage.setItem("cart", JSON.stringify(state.cart));
    validateMultiCombos();

    alert(`Promo "${promo.nombre}" agregada correctamente.`);

  } catch (error) {
    console.error("❌ Error al agregar promoción:", error);
    alert("No se pudo agregar la promoción.");
  }
};

// ----------------------------------------------------------
// ELIMINAR
// ----------------------------------------------------------
const removeFromCart = (itemOrIndex) => {
  let index = itemOrIndex;

  if (typeof itemOrIndex === 'object' && itemOrIndex !== null) {
    const rSku = itemOrIndex.sku ?? String(itemOrIndex.producto_id ?? itemOrIndex.id);
    index = state.cart.findIndex(
      (p) => p.sku === rSku && !!p.isComboItem === !!itemOrIndex.isComboItem && !!p.isGift === !!itemOrIndex.isGift
    );
  }

  if (index !== -1 && index < state.cart.length) {
    state.cart.splice(index, 1);
    validateMultiCombos();
  }
};

const removeComboFromCart = (comboId) => {

  state.cart = state.cart.filter(p => p.comboId !== comboId);
  validateMultiCombos();

};

// ----------------------------------------------------------
// BLOQUEAR + / - PARA PROMOCIONES 🔒
// ----------------------------------------------------------
const increaseQuantity = (item) => {

  // 🛑 VALIDACIÓN: Bloquear combos prorrateados, promociones especiales y regalos
  if (item.isLocked || item.isComboItem || item.isGift) {
    alert("Esta promoción o combo no permite modificar cantidades individualmente.");
    return;
  }

  const sku = item.sku ?? String(item.producto_id ?? item.id);
  const existingProduct = state.cart.find(
    (product) => product.sku === sku && !!product.isComboItem === !!item.isComboItem && !!product.isGift === !!item.isGift
  );

  if (!existingProduct) return;

  if (existingProduct.quantity >= 40) {
    alert("Solo puedes agregar hasta 40 unidades.");
    return;
  }

  existingProduct.quantity++;
  validateMultiCombos();
};



const decreaseQuantity = (item) => {
  // 🛑 VALIDACIÓN: Bloquear resta para combos prorrateados o regalos
  if (item.isLocked || item.isComboItem || item.isGift) return;

  const dSku = item.sku ?? String(item.producto_id ?? item.id);
  const index = state.cart.findIndex(
    (product) => product.sku === dSku && !!product.isComboItem === !!item.isComboItem && !!product.isGift === !!item.isGift
  );

  if (index === -1) return;

  if (state.cart[index].quantity > 1) {
    state.cart[index].quantity--;
  } else {
    state.cart.splice(index, 1);
  }

  validateMultiCombos();
};
// ----------------------------------------------------------
const getCartTotal = () => {
  return state.cart.reduce((total, item) => {
    if (item.sku && item.sku.startsWith('PP')) return total;
    const price = item.precio_con_descuento || item.price;
    return total + price * item.quantity;
  }, 0).toFixed(2);
};

const getTotalCajas = () => {
  return state.cart.reduce((total, item) => total + item.quantity, 0);
};

const getCartCoinsTotal = () => {
  return state.cart.reduce((total, item) => {
    return total + (item.mvCoinsCost || 0) * item.quantity;
  }, 0);
};

const getProductQuantity = (product) => {
  if (!product) return 0;
  const sku = product.sku ?? String(product.producto_id ?? product.id);
  const existing = state.cart.find(
    (item) => item.sku === sku && !!item.isComboItem === !!product.isComboItem && !!item.isGift === !!product.isGift
  );
  return existing ? existing.quantity : 0;
};

// ----------------------------------------------------------
const checkout = () => {
  const total = state.cart.reduce((acc, item) => {
    if (item.sku?.startsWith('PP')) return acc;
    const price = item.precio_con_descuento || item.price;
    return acc + price * item.quantity;
  }, 0);

  const rounded = parseFloat(total.toFixed(2));
  localStorage.setItem('checkoutTotal', rounded);
  alert(`Total a pagar: $${rounded}`);
  return rounded;
};

// =========================
// VALIDAR MULTICOMBOS
// =========================
const validateMultiCombos = (comboDefsInput) => {
  if (Array.isArray(comboDefsInput) && comboDefsInput.length > 0) {
    multicombos.value = [...comboDefsInput];
  }
  const comboDefs = multicombos.value;

  if (!Array.isArray(comboDefs)) return;

  comboDefs.forEach(combo => {
    const comboId = `multicombo-${combo.id}`;

    const requiredItems = combo.items.filter(i => i.tipo === "PRINCIPAL");
    const giftItems = combo.items.filter(i => i.tipo === "REGALO");

    // ============================================================
    // ✔ 1) CONTAR PRINCIPALES Y VALIDAR QUE SE CUMPLA EL COMBO EXACTO
    // ============================================================
    // Se requiere cumplir la cantidad especificada de CADA producto principal
    const combosPossibles = requiredItems.map(req => {
      let qtyInCart = 0;
      state.cart.forEach(cartItem => {
        // MUY IMPORTANTE: Excluir regalos para que promos 1+1 no se sumen a sí mismas infinitamente
        if (Number(cartItem.id) === Number(req.producto_id) && !cartItem.isGift) {
          qtyInCart += cartItem.quantity;
        }
      });
      // Cuántas veces se cubre el requerimiento de ESTE producto específico
      return Math.floor(qtyInCart / (req.cantidad || 1));
    });

    // El número final de combos corresponde a la cantidad limitante más baja (combo completo)
    const numCombosGenerados = combosPossibles.length > 0 ? Math.min(...combosPossibles) : 0;
    const cumple = numCombosGenerados > 0;

    // ============================================================
    // ✔ 2) SI ES REGALO MANUAL → NO tocarlo
    // ============================================================
    const hasManualGift = state.cart.some(i =>
      i.comboId === comboId && i.isGiftFromPromotion
    );
    if (hasManualGift) return;

    // ============================================================
    // ✔ 3) MANEJO DEL PRECIO FIJO & REGALOS AUTOMÁTICOS
    // ============================================================
    if (cumple) {
      // 1. Aplicar precio fijo a todos los items principales de este combo
      requiredItems.forEach(req => {
        state.cart.forEach(cartItem => {
          if (
            Number(cartItem.id) === Number(req.producto_id) &&
            req.precio_fijo != null &&
            !cartItem.isGift &&
            (!cartItem.comboId || cartItem.comboId === comboId)
          ) {
            if (cartItem.originalPrice === undefined) {
              cartItem.originalPrice = cartItem.price;
            }
            cartItem.price = Number(req.precio_fijo);
            cartItem.comboId = comboId;
            cartItem.isComboItem = true;
            cartItem.folioPromocion = combo.folio || null;
          }
        });
      });

      // 2. Dar el regalo (o descuento con pagos en MV Coins si aplica)
      giftItems.forEach(gift => {
        const isMVCoinsReward = combo.tipo === "GIFT" || (combo.nombre && combo.nombre.toUpperCase().includes("MV COIN"));
        let giftFixPrice = gift.precio_fijo != null ? Number(gift.precio_fijo) : 0;

        // Si es una recompensa por monedas pero la API mandó 0.00 como costo, forzamos la deducción a 30 por defecto como se solicitó
        if (isMVCoinsReward && giftFixPrice === 0) giftFixPrice = 30;
        const existsGift = state.cart.find(p =>
          p.comboId === comboId &&
          p.isGift === true &&
          p.sku === gift.producto.sku
        );
        const desiredQty = numCombosGenerados * (gift.cantidad || 1);

        if (!existsGift) {
          state.cart.push({
            ...gift.producto,
            sku: gift.producto.sku,
            producto_id: gift.producto.id,
            comboId,
            isGift: true,
            isGiftFromPromotion: false,
            tipo: 'REGALO',
            isMVCoinsReward: isMVCoinsReward, // 🔥 Flag para interfaz
            mvCoinsCost: isMVCoinsReward ? giftFixPrice : 0,  // 🔥 El costo en monedas
            isLocked: true, // Bloquea modificación para cumplir regla del admin
            originalPrice: giftFixPrice,
            price: isMVCoinsReward ? 0 : giftFixPrice, // ✅ Sujeto a sumar solo $0 en el subtotal
            quantity: desiredQty,
            nombre: gift.producto.nombre + (isMVCoinsReward ? " 🪙" : (giftFixPrice > 0 ? " (Promo)" : " 🎁")),
            folioPromocion: combo.folio || null
          });
        } else {
          // Ajusta cantidad en caso de ganar multiples combos
          existsGift.quantity = desiredQty;
          existsGift.price = isMVCoinsReward ? 0 : giftFixPrice;
          existsGift.mvCoinsCost = isMVCoinsReward ? giftFixPrice : 0;
          existsGift.isMVCoinsReward = isMVCoinsReward;
          existsGift.folioPromocion = combo.folio || null;
        }
      });

    } else {
      // ❌ Restablecer precios de los principales (ya no alcanza combo)
      requiredItems.forEach(req => {
        state.cart.forEach(cartItem => {
          if (Number(cartItem.id) === Number(req.producto_id) && cartItem.isComboItem && cartItem.comboId === comboId) {
            cartItem.price = cartItem.originalPrice !== undefined ? cartItem.originalPrice : cartItem.price;
            cartItem.isComboItem = false;
            cartItem.comboId = null;
            cartItem.folioPromocion = null;
          }
        });
      });

      // ❌ Eliminar regalos y promociones generadas por este combo
      state.cart = state.cart.filter(item =>
        !(item.comboId === comboId && item.isGift && !item.isGiftFromPromotion)
      );
    }
  });

  // ============================================================
  // ✔ 5) ORDENAR REGALOS AL FINAL DEL CARRITO
  // ============================================================
  state.cart.sort((a, b) => {
    if (a.isGift && !b.isGift) return 1;
    if (!a.isGift && b.isGift) return -1;
    return 0;
  });

  localStorage.setItem("cart", JSON.stringify(state.cart));
};




// =========================
// ROMPER PROMO — solo revierte precio y desbloquea ítems del combo, no elimina productos normales
// =========================
const breakPromo = (comboId) => {
  // 1. Revertir precio de catálogo y limpiar todos los campos del combo/promo
  state.cart.forEach(item => {
    if (item.comboId === comboId && !item.isGift) {
      item.price = item.originalPrice !== undefined ? item.originalPrice : item.price;
      item.isComboItem = false;
      item.isLocked = false;
      item.comboId = null;
      item.folioPromocion = null;
      // Limpiar indicadores de descuento para que el template muestre precio normal
      item.id_promocion = null;
      item.promo_price = null;
      item.promoUnico = false;
    }
  });

  // 2. Eliminar solo los regalos generados por este combo
  state.cart = state.cart.filter(
    item => !(item.comboId === comboId && item.isGift)
  );

  localStorage.setItem('cart', JSON.stringify(state.cart));
};

// =========================
// PRECIO DE CATÁLOGO — busca en todos los cachés de productos del localStorage
// =========================
const getCatalogPrice = (productId, sku) => {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('products_')) continue;
    try {
      const data = JSON.parse(localStorage.getItem(key));
      const products = Array.isArray(data) ? data : (data?.products ?? []);
      const found = products.find(p =>
        (productId && (p.producto_id === productId || p.id === productId)) ||
        (sku && p.sku === sku)
      );
      if (found && Number(found.price) > 0) return Number(found.price);
    } catch { /* caché corrupto, ignorar */ }
  }
  return null;
};

// =========================
// AGREGAR COMBO (PROMO COMPLETA) — LÍMITE GLOBAL: 1 VEZ POR COMBO
// =========================
const addComboToCart = (combo) => {
  const comboId = `multicombo-${combo.id}`;
  const isDescuento = combo.tipo === 'DESCUENTO';

  if (isDescuento) {
    const anySkuLocked = combo.items.some(i => {
      if (i.tipo?.toUpperCase() === 'REGALO') return false;
      const sku = i.producto?.sku ?? '';
      return state.cart.some(c => c.sku === sku && (c.isComboItem || c.isLocked));
    });
    if (anySkuLocked) return false;
  } else {
    const alreadyInCart = state.cart.some(
      item => item.comboId === comboId && item.isComboItem
    );
    if (alreadyInCart) return false;
  }

  // Agregar TODOS los items (PRINCIPAL + REGALO) directamente desde los datos del combo.
  // No depender de validateMultiCombos para los regalos — esa función requiere
  // que multicombos.value ya esté cargado, lo cual es una condición de carrera.
  combo.items.forEach(i => {
    const isGift = i.tipo?.toUpperCase() === 'REGALO';
    const productId = i.producto_id || i.producto?.id;
    const sku = i.producto?.sku ?? String(productId);

    const catalogPrice = isGift
      ? Number(i.precio_fijo)
      : (getCatalogPrice(productId, sku) ||
         Number(i.producto?.precio_1 || i.producto?.price ||
                i.producto?.precio_lista || i.producto?.precio_mostrador ||
                i.precio_fijo));

    const nombreSufijo = isGift
      ? (Number(i.precio_fijo) === 0 ? ' 🎁' : ' (Promo)')
      : '';

    for (let j = 0; j < i.cantidad; j++) {
      state.cart.push({
        ...i.producto,
        sku,
        precio: i.precio_fijo,
        price: Number(i.precio_fijo),
        originalPrice: catalogPrice,
        isComboItem: !isGift,
        isGift,
        isLocked: true,
        comboId,
        quantity: 1,
        tipo: i.tipo || 'PRINCIPAL',
        folioPromocion: isDescuento ? null : (combo.folio || null),
        nombre: (i.producto?.nombre ?? '') + nombreSufijo,
        iva: Number(i.producto?.iva16 ?? i.producto?.iva ?? 0),
        ieps: Number(i.producto?.ieps ?? 0),
      });
    }
  });

  // Registrar el combo en multicombos para que validateMultiCombos
  // pueda ajustar cantidades en cambios futuros del carrito.
  if (!multicombos.value.some(m => m.id === combo.id)) {
    multicombos.value = [...multicombos.value, combo];
  }

  validateMultiCombos();
  if (navigator.vibrate) navigator.vibrate(50);
  return true;
};

//CARGAR MULTICOMBOS
// =========================
const loadMulticombos = async () => {
  try {
    const { value } = await Preferences.get({ key: 'auth_session' });
    const token = value ? JSON.parse(value).token : null;

    const res = await axios.get(
      "https://mvexpress.mx/api/promotions/promos/multicombo",
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    // 🔒 aseguramos que sea array
    multicombos.value = Array.isArray(res.data.combos)
      ? res.data.combos
      : [];

    console.log("Multicombos cargados:", multicombos.value);

    // Evaluate if there are items in the cart that qualify for combo pricing
    if (state.cart.length > 0) {
      validateMultiCombos();
    }

  } catch (err) {
    console.error("❌ Error al cargar multicombos", err);
    multicombos.value = [];
  }
};


// =========================
// REVERTIR PROMO SIMPLE — devuelve precio original a todos los ítems con el mismo id_promocion
// =========================
const revertPromoById = (promoId) => {
  if (!promoId) return;
  state.cart.forEach(item => {
    if (item.id_promocion === promoId && !item.isComboItem && !item.isGift) {
      if (item.promo_price > 0) {
        item.price = Number((item.price / (1 - item.promo_price / 100)).toFixed(2));
      }
      item.id_promocion = null;
      item.promo_price = null;
      item.promoUnico = false;
    }
  });
  localStorage.setItem('cart', JSON.stringify(state.cart));
};

// =========================
// INICIALIZAR COMBOS
// =========================

let _cartStoreInstance = null;

export function createCartStore() {
  if (_cartStoreInstance) return _cartStoreInstance;

  // No-blocking: carga multicombos después del primer frame para no retrasar el inicio
  setTimeout(() => loadMulticombos(), 0);

  _cartStoreInstance = {
    state,
    resetState,
    lastUpdated,
    loadCartFromLocalStorage,
    addToCart,
    addToCart2,
    addGiftToCart,
    removeFromCart,
    removeComboFromCart,
    clearCart: resetState,
    getCartTotal,
    increaseQuantity,
    decreaseQuantity,
    getTotalCajas,
    getProductQuantity,
    checkout,
    validateMultiCombos,
    loadMulticombos,
    multicombos,
    getCartCoinsTotal,
    addComboToCart,
    breakPromo,
    revertPromoById
  };
  return _cartStoreInstance;
}
